import { FC, useEffect, useMemo } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import { ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { TrustSignals } from '@dbt-labs/metadata-shared';
import {
  Badge,
  Breadcrumbs,
  Button,
  PaginationFooter,
  Sizes,
} from '@dbt-labs/sourdough';

import {
  ExposureHealthIssueType,
  HealthIssueType,
  SearchFieldType,
  SourceHealthIssueType,
} from '@/__generated__/graphql';
import { PageHeading } from '@/components/PageHeading';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { NodeFilters, useFilterContext } from '@/context/FilterProvider';
import { useProjectBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { pluralizeResourceType } from '@/util/resourceType';
import { toTitleCase } from '@/util/string';
import { SearchResultItem } from './SearchResultItem';

export const getResultCountString = (count: number | undefined) => {
  if (count === undefined) return null;
  if (count === 1) return '1 result';
  return `${count} results`;
};

export type SearchResultHit = {
  name: string | null;
  uniqueId: string;
  resourceType: ResourceTypeExplorer;
  fqn?: string[];
  modelHealthIssues?: HealthIssueType[];
  sourceHealthIssues?: SourceHealthIssueType[];
  freshness?: {
    freshnessChecked: boolean | null;
  };
  exposureHealthIssues?: ExposureHealthIssueType[];
};

export type SearchResultDisplayData = {
  matchedField: SearchFieldType;
  highlight: string | null;
  hit: SearchResultHit;
  tags?: never;
  columns?: never;
};

export function getTrustSignalsFromHit(hit: SearchResultHit): TrustSignals {
  switch (hit.resourceType) {
    case 'model':
      return { healthIssues: hit.modelHealthIssues ?? [] };
    case 'source':
      return {
        healthIssues: hit.sourceHealthIssues ?? [],
        additionalMetadata: { freshnessChecked: hit.freshness?.freshnessChecked },
      };
    case 'exposure':
      return { healthIssues: hit.exposureHealthIssues ?? [] };
    default:
      return { healthIssues: [] };
  }
}

export interface SearchResultsParams {
  pageSize: number;
  query: string;
  title: string;
  isLoadingMore: boolean;
  skeleton?: boolean;
  hasMoreResults: boolean;
  data: SearchResultDisplayData[] | undefined;
  totalCount: number | undefined;
  fetchMore: () => void;
  testId?: string;
}

export const SearchResultsList: FC<
  Omit<SearchResultsParams, 'title' | 'error' | 'totalCount'>
> = ({
  query,
  testId = 'search-result',
  isLoadingMore,
  skeleton,
  hasMoreResults,
  pageSize,
  data,
  fetchMore,
}) => {
  const displayedData = useMemo(() => {
    let displayedData: (SearchResultDisplayData | 'skeleton')[] | undefined = data
      ? [...data]
      : undefined;
    if (skeleton || isLoadingMore) {
      displayedData ??= new Array<'skeleton'>();
      const skeletonElements = new Array<'skeleton'>(pageSize).fill('skeleton');
      displayedData.push(...skeletonElements);
    }

    return displayedData;
  }, [data, isLoadingMore, pageSize, skeleton]);

  return (
    <div className="space-y-2">
      {displayedData?.map((searchResult, index) => {
        if (searchResult === 'skeleton') {
          return <SearchResultItem key={`${skeleton}-${index}`} skeleton />;
        }
        return (
          <SearchResultItem
            key={searchResult.hit.uniqueId}
            query={query}
            data={searchResult}
            testId={`${testId}-${index}`}
          />
        );
      })}
      {displayedData?.length === 0 && <div>No results were found</div>}
      {hasMoreResults && (
        <PaginationFooter
          hasMorePages={hasMoreResults}
          onLoadMore={fetchMore}
          isPageLoading={isLoadingMore || skeleton}
        />
      )}
    </div>
  );
};

const NoSearchResults: FC<SearchResultsParams> = (props) => {
  useEffect(() => {
    datadogRum.addAction('explorer-no-search-results');
  }, []);
  return (
    <div className="my-8">
      <h1 className="mx-auto mb-2.5 w-fit text-4xl font-medium text-fgMain">
        No search results for "{props.query || props.title}"
      </h1>
      <div className="mx-auto w-fit text-fgDecorative">
        We couldn't find exactly what you were looking for.
      </div>
    </div>
  );
};

const SearchFilterPills: FC = () => {
  const filterCtx = useFilterContext();

  if (filterCtx.activeFilterCount === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(
        [
          'resourceType',
          'column',
          'access',
          'tags',
          'materialization',
          'searchFields',
        ] satisfies (keyof NodeFilters)[]
      ).map((filterName) => {
        if (filterName === 'column') {
          if (!filterCtx.filters.column) return null;
          return (
            <Badge
              key={filterName}
              size="xl"
              text={pluralizeResourceType('column', true)}
              onRemove={() => filterCtx.updateFilters({ [filterName]: false }, false)}
            />
          );
        }
        return filterCtx.filters[filterName]?.map((item) => (
          <Badge
            key={`${filterName}-${item}`}
            size="xl"
            text={
              filterName === 'resourceType'
                ? pluralizeResourceType(item as ResourceTypeExplorer, true)
                : toTitleCase(item)
            }
            onRemove={() => filterCtx.updateFilters({ [filterName]: [item] }, false)}
          />
        ));
      })}

      <Button size={Sizes.sm} type="link" onClick={filterCtx.clearFilters}>
        Clear all
      </Button>
    </div>
  );
};

export const SearchResults: FC<SearchResultsParams> = (props) => {
  const { accountId } = useDbtCloudAccount();
  const breadcrumbs = useProjectBreadcrumbs();

  if (props?.data?.length === 0) {
    return <NoSearchResults {...props} />;
  }

  if (!accountId) {
    return null;
  }

  const resultCount = props?.data?.length;
  return (
    <>
      <Breadcrumbs className="mb-3" breadcrumbs={breadcrumbs} />
      <PageHeading className="mb-2">{props.title}</PageHeading>
      {(resultCount === undefined || resultCount > 0) && (
        <div className="mb-4 space-y-4 text-fgDecorative">
          {resultCount !== undefined && (
            <div>{getResultCountString(props.totalCount)}</div>
          )}

          <SearchFilterPills />
        </div>
      )}
      <div>
        <SearchResultsList {...props} />
      </div>
    </>
  );
};
